import { authMiddlewares } from '@/middlewares/auth.middlewares';
import { AppType, useAppStore } from '@/stores/app.store';
import type { RouteRecordRaw } from 'vue-router';
import { useProjectStore } from '../stores/project.store';
import { useOrderSummaryStore } from '@/stores/orderSummary.store';
import { ProjectLandkitIntegrationStatus } from '../interfaces/project.interface';
import { projectMiddlewares } from '@/middlewares/project.middlewares';

export default [
  {
    path: '',
    component: () => import('@/modules/cleepo/layouts/DashboardLayout.vue'),
    beforeEnter: [
      () => {
        useAppStore().setCurrentApp(AppType.Cleepo);
      },
      authMiddlewares.isAuthenticatedCleepo,
      authMiddlewares.isAccountConfigured,
      authMiddlewares.isCleepoAccount,
    ],
    children: [
      {
        path: '',
        beforeEnter: [
          (to, from, next) => {
            if (
              useProjectStore().currentProject?.landkit_integration?.status ===
              ProjectLandkitIntegrationStatus.DRAFT
            ) {
              return next({ name: 'CleepoProjectView' });
            }
            return next();
          },
          projectMiddlewares.hasPendingRequiredConfigurationTasks,
        ],
        name: 'CleepoDashboardView',
        meta: { title: 'nav_menu.dashboard' },
        component: () => import('@/modules/cleepo/views/common/DashboardView.vue'),
      },
      {
        path: 'interactions',
        beforeEnter: [projectMiddlewares.hasPendingRequiredConfigurationTasks],
        name: 'CleepoInteractionsView',
        meta: { title: 'nav_menu.interactions' },
        component: () => import('@/modules/cleepo/views/common/InteractionsView.vue'),
      },
      {
        path: 'contacts',
        beforeEnter: [projectMiddlewares.hasPendingRequiredConfigurationTasks],
        name: 'CleepoContactsView',
        meta: { title: 'nav_menu.contacts' },
        component: () => import('@/modules/cleepo/views/common/ContactsView.vue'),
      },
      {
        path: 'project',
        beforeEnter: [projectMiddlewares.hasPendingRequiredConfigurationTasks],
        name: 'CleepoProjectView',
        meta: { title: 'nav_menu.project' },
        component: () => import('@/modules/cleepo/views/common/ProjectView.vue'),
      },
      {
        path: 'landing',
        beforeEnter: [projectMiddlewares.hasPendingRequiredConfigurationTasks],
        name: 'CleepoLandingView',
        meta: { title: 'nav_menu.landing' },
        component: () => import('@/modules/cleepo/views/common/LandingView.vue'),
      },
      {
        path: 'domain',
        beforeEnter: [projectMiddlewares.hasPendingRequiredConfigurationTasks],
        name: 'CleepoDomainView',
        meta: { title: 'nav_menu.domain' },
        component: () => import('@/modules/cleepo/views/common/DomainView.vue'),
      },
    ],
  },
  {
    path: 'landing-editor',
    component: () => import('@/modules/cleepo/layouts/LandingEditorLayout.vue'),
    beforeEnter: [
      authMiddlewares.isAuthenticatedCleepo,
      authMiddlewares.isAccountConfigured,
      authMiddlewares.isCleepoAccount,
    ],
    meta: { title: 'cleepo.landing_editor.title' },
    children: [
      {
        path: '',
        name: 'CleepoLandingEditorView',
        component: () => import('@/modules/cleepo/views/landing/LandingEditorView.vue'),
      },
    ],
  },
  {
    path: 'invest',
    component: () => import('@/modules/cleepo/layouts/DashboardBlankLayout.vue'),
    meta: { title: 'nav_menu.investment' },
    beforeEnter: [
      authMiddlewares.isAuthenticatedCleepo,
      authMiddlewares.isAccountConfigured,
      authMiddlewares.isCleepoAccount,
      projectMiddlewares.hasPendingRequiredConfigurationTasks,
      (to, from, next) => {
        useAppStore().setCurrentApp(AppType.Cleepo);
        const orderSummaryStore = useOrderSummaryStore();
        if (!orderSummaryStore.hasFreePlanActive) {
          next({ name: 'AccountSettingsPlanView' });
        }
        next();
      },
    ],
    children: [
      {
        path: '',
        name: 'CleepoInvestView',
        component: () => import('@/modules/cleepo/views/common/InvestView.vue'),
      },
    ],
  },
  {
    path: 'auth',
    component: () => import('@/layouts/BlankLayout.vue'),
    redirect: 'auth/login',
    beforeEnter: [
      () => {
        useAppStore().setCurrentApp(AppType.Cleepo);
      },
      authMiddlewares.isAppLogin,
    ],
    children: [
      {
        path: 'login',
        name: 'CleepoAuthLoginView',
        beforeEnter: [authMiddlewares.shouldRedirectToDashboard],
        meta: { title: 'auth.sign_in.title' },
        component: () => import('@/modules/cleepo/views/auth/AuthLoginView.vue'),
      },
      {
        path: 'email',
        name: 'CleepoAuthEmailView',
        beforeEnter: [authMiddlewares.shouldRedirectToDashboard],
        meta: { title: 'auth.sign_in_email.title' },
        props: (route) => ({
          email: route.query.email ?? '',
        }),
        component: () => import('@/modules/cleepo/views/auth/AuthEmailView.vue'),
      },
      {
        path: 'register',
        name: 'CleepoAuthRegisterView',
        beforeEnter: [authMiddlewares.shouldRedirectToDashboard],
        meta: { title: 'auth.sign_up.title' },
        component: () => import('@/modules/cleepo/views/auth/AuthRegisterView.vue'),
      },

      {
        path: 'recover-password',
        meta: { title: 'auth.recover_password.title' },
        component: () => import('@/layouts/BaseLayout.vue'),
        children: [
          {
            path: '',
            name: 'CleepoAuthRecoverPasswordView',
            props: (route) => ({
              email: route.query.email ?? '',
            }),
            component: () => import('@/modules/cleepo/views/auth/AuthRecoverPasswordView.vue'),
          },
        ],
      },
      {
        path: 'reset-password',
        meta: { title: 'auth.reset_password.title' },
        component: () => import('@/layouts/BaseLayout.vue'),
        children: [
          {
            path: '',
            name: 'CleepoAuthResetPasswordView',
            props: (route) => ({
              token: route.query.token ?? '',
            }),
            component: () => import('@/modules/cleepo/views/auth/AuthResetPasswordView.vue'),
          },
        ],
      },
    ],
  },
  {
    path: 'wizard',
    component: () => import('@/layouts/BlankLayout.vue'),
    redirect: 'wizard/create-account',
    beforeEnter: [
      authMiddlewares.isAuthenticatedCleepo,
      () => {
        useAppStore().setCurrentApp(AppType.Cleepo);
      },
    ],
    children: [
      {
        path: 'create-account',
        name: 'CleepoWizardCreateAccountView',
        meta: { title: 'cleepo.wizard_create_account.title' },
        component: () => import('@/modules/cleepo/views/wizard/WizardCreateAccountView.vue'),
      },
      {
        path: 'interview',
        meta: { title: 'cleepo.wizard_interview.title' },
        component: () => import('@/modules/cleepo/layouts/WizardLayout.vue'),
        children: [
          {
            path: '',
            name: 'CleepoWizardInterviewView',
            component: () => import('@/modules/cleepo/views/wizard/WizardInterviewView.vue'),
          },
        ],
      },
      {
        path: 'project-brief',
        meta: { title: 'cleepo.wizard_project_brief.title' },
        component: () => import('@/modules/cleepo/layouts/WizardLayout.vue'),
        children: [
          {
            path: '',
            name: 'CleepoWizardProjectBriefView',
            component: () => import('@/modules/cleepo/views/wizard/WizardProjectBriefView.vue'),
          },
        ],
      },
      {
        path: 'configuration-tasks',
        name: 'CleepoWizardConfigurationTasksView',
        meta: { title: 'cleepo.wizard_configuration_tasks.title' },
        component: () => import('@/modules/cleepo/layouts/WizardLayout.vue'),
        children: [
          {
            path: '',
            name: 'CleepoWizardConfigurationTasksView',
            component: () =>
              import('@/modules/cleepo/views/wizard/WizardConfigurationTasksView.vue'),
          },
        ],
      },
    ],
  },
  {
    path: 'site-suspended',
    component: () => import('@/layouts/BaseLayout.vue'),
    children: [
      {
        path: '',
        name: 'CleepoSiteSuspendedView',
        component: () => import('@/modules/cleepo/views/common/SiteSuspendedView.vue'),
      },
    ],
  },
] satisfies RouteRecordRaw[];
